import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ShareButtons from '../components/ShareButtons';
import RelatedNews from '../components/RelatedNews';
import { newsAPI } from '../utils/api/newsAPI';
import { hasTrackedView, markTrackedView, formatEngagementCount } from '../utils/contentEngagement';
import './NewsDetail.css';

/**
 * NewsDetail Page
 * Displays full news article with details, sharing options, and related news
 */
function NewsDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [news, setNews] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);

    const fetchNewsDetail = async () => {
      try {
        setLoading(true);
        setError(null);

        const data = await newsAPI.getNewsBySlug(slug);

        if (data.success) {
          setNews(data.data);
        } else {
          setError('समाचार नहीं मिला');
        }
      } catch (fetchError) {
        console.error('Error fetching news:', fetchError);
        setError('समाचार लोड करने में विफल रहे');
      } finally {
        setLoading(false);
      }
    };

    fetchNewsDetail();
  }, [slug]);

  useEffect(() => {
    const previousTitle = document.title;

    const updateMeta = (name, content, attribute = 'name') => {
      if (!content) {
        return null;
      }

      let element = document.head.querySelector(`meta[${attribute}="${name}"]`);
      let created = false;

      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, name);
        document.head.appendChild(element);
        created = true;
      }

      const previousContent = element.getAttribute('content');
      element.setAttribute('content', content);

      return {
        element,
        previousContent,
        created
      };
    };

    if (!news) {
      document.title = 'समाचार | लाठ साब';
      return () => {
        document.title = previousTitle;
      };
    }

    document.title = `${news.title} | लाठ साब`;

    const updates = [
      updateMeta('description', news.shortDescription),
      updateMeta('keywords', news.tags?.join(', ')),
      updateMeta('og:title', news.title, 'property'),
      updateMeta('og:description', news.shortDescription, 'property'),
      updateMeta('og:image', news.image, 'property'),
      updateMeta('og:url', `${window.location.origin}/news/${news.slug}`, 'property'),
      updateMeta('og:type', 'article', 'property'),
      updateMeta('twitter:card', 'summary_large_image', 'property'),
      updateMeta('twitter:title', news.title, 'property'),
      updateMeta('twitter:description', news.shortDescription, 'property'),
      updateMeta('twitter:image', news.image, 'property')
    ].filter(Boolean);

    return () => {
      document.title = previousTitle;

      updates.forEach(({ element, previousContent, created }) => {
        if (created) {
          element.remove();
          return;
        }

        if (previousContent === null) {
          element.removeAttribute('content');
          return;
        }

        element.setAttribute('content', previousContent);
      });
    };
  }, [news]);

  useEffect(() => {
    if (!news?._id || hasTrackedView('news', news._id)) {
      return;
    }

    const trackView = async () => {
      try {
        const response = await newsAPI.incrementNewsView(news._id);

        if (response.success && response.data?.views !== undefined) {
          setNews((current) => current ? { ...current, views: response.data.views } : current);
        }

        markTrackedView('news', news._id);
      } catch (error) {
        console.error('Failed to increment news view count:', error);
      }
    };

    trackView();
  }, [news?._id]);

  if (loading) {
    return (
      <div className="news-detail loading">
        <div className="spinner">लोड हो रहा है...</div>
      </div>
    );
  }

  if (error || !news) {
    return (
      <div className="news-detail error">
        <div className="error-container">
          <h2>त्रुटि</h2>
          <p>{error || 'समाचार नहीं मिला'}</p>
          <button className="back-btn" onClick={() => navigate('/')}>
            होम पर जाएं
          </button>
        </div>
      </div>
    );
  }

  const newsUrl = `${window.location.origin}/news/${news.slug}`;

  return (
    <>
      <article className="news-detail">
        <div className="news-detail-container">
          {/* Main Content */}
          <div className="news-detail-main">
            {/* Breadcrumb */}
            <nav className="breadcrumb">
              <a href="/">होम</a>
              <span>/</span>
              <a href={`/category/${news.categoryName?.toLowerCase()}`}>
                {news.categoryName}
              </a>
              <span>/</span>
              <span>{news.title.substring(0, 50)}...</span>
            </nav>

            {/* Header */}
            <header className="news-header">
              {news.isBreaking && <span className="breaking-banner">ब्रेकिंग न्यूज़</span>}
              <h1 className="news-title">{news.title}</h1>

              <div className="news-meta">
                <span className="category-badge">{news.categoryName}</span>
                {news.district && <span className="location-badge"> {news.district}</span>}
                {news.block && <span className="location-badge">{news.block}</span>}
                <span className="date-badge">
                   {new Date(news.createdAt).toLocaleDateString('hi-IN', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </span>
                <span className="views-badge">👁️ {formatEngagementCount(news.views)} views</span>
                <span className="views-badge">🔗 {formatEngagementCount(news.shareCount)} shares</span>
              </div>

              <p className="news-short-description">{news.shortDescription}</p>
            </header>

            {/* Featured Image */}
            <figure className="news-feature-image">
              <img 
                src={news.image} 
                alt={news.title}
                loading="lazy"
              />
              <figcaption>समाचार छवि</figcaption>
            </figure>

            {/* Content */}
            <div className="news-content">
              <p>{news.description}</p>
            </div>

            {/* Video if available */}
            {news.videoUrl && (
              <div className="news-video">
                <iframe
                  width="100%"
                  height="500"
                  src={news.videoUrl}
                  title={news.title}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            )}

            {/* Tags */}
            {news.tags && news.tags.length > 0 && (
              <div className="news-tags">
                {news.tags.map((tag, index) => (
                  <a 
                    key={index} 
                    href={`/search?q=${encodeURIComponent(tag)}`}
                    className="tag"
                  >
                    #{tag}
                  </a>
                ))}
              </div>
            )}

            {/* Share Buttons */}
            <ShareButtons 
              title={news.title}
              url={newsUrl}
              description={news.shortDescription}
              contentId={news._id}
              contentType="news"
            />

            {/* Related News */}
            <RelatedNews newsId={news._id} />
          </div>

          {/* Sidebar */}
          <aside className="news-detail-sidebar">
            {/* Sticky Ad or Widget */}
            <div className="sidebar-widget">
              <h4>महत्वपूर्ण संदर्भ</h4>
              {news.district && <p className="info-item">जिला: <strong>{news.district}</strong></p>}
              {news.block && <p className="info-item">ब्लॉक: <strong>{news.block}</strong></p>}
              {news.village && <p className="info-item">गांव: <strong>{news.village}</strong></p>}
              <p className="info-item">श्रेणी: <strong>{news.categoryName}</strong></p>
            </div>

            {/* Share Widget */}
            <div className="sidebar-widget">
              <h4>इस पृष्ठ को साझा करें</h4>
              <ShareButtons
                title={news.title}
                url={newsUrl}
                description={news.shortDescription}
                contentId={news._id}
                contentType="news"
                compact
              />
            </div>
          </aside>
        </div>
      </article>
    </>
  );
}

export default NewsDetail;
